import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getCollection, MUSIC_PAGE_ASSETS_ENTRY_ID } from '@/services/CmsService/Collection'
import { LinkViewModel } from '@/services/RenderService'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { MusicView } from '@/views/MusicView'
import { AdditionalMusicVideo, HeroImages, MusicVideo, SeriesMusical } from '@/views/MusicView/types'

interface MusicPageProps {
  locale: string
  preview: boolean
  heroImages: HeroImages
  musicVideos: MusicVideo[]
  soundtracks: LinkViewModel[]
  seriesMusicals: SeriesMusical[]
  additionalMusicVideos: AdditionalMusicVideo[]
}

const MusicPage: NextPageWithLayout<MusicPageProps> = ({
  locale,
  preview,
  heroImages,
  musicVideos,
  soundtracks,
  seriesMusicals,
  additionalMusicVideos,
}) => {
  const { t } = useTranslate('music')
  const basePath = `${getBasePath(locale)}`
  const url = `${basePath}${paths.music.index}`
  const title = t('angelStudiosMusicTitle', 'The Music of Angel')
  const description = t(
    'angelStudiosMusicMetaDescription',
    'Experience the original music and scores from some of your favorite Angel Films and Series!',
  )

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.music.index}
        title={title}
      />
      <MusicView
        locale={locale}
        preview={preview}
        heroImages={heroImages}
        musicVideos={musicVideos}
        soundtracks={soundtracks}
        seriesMusicals={seriesMusicals}
        additionalMusicVideos={additionalMusicVideos}
      />
    </>
  )
}

MusicPage.getLayout = (page) => {
  return <DefaultLayout>{page}</DefaultLayout>
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<MusicPageProps>(
  { filePath: 'pages/[locale]/music/index.tsx' },
  async ({ locale, preview }) => {
    const [musicCollection, translations] = await Promise.all([
      getCollection({ locale, preview }, MUSIC_PAGE_ASSETS_ENTRY_ID),
      loadTranslations(locale, ['common', 'music']),
    ])

    const heroImages = musicCollection?.customData?.heroImages ?? []
    const musicVideos = musicCollection?.customData?.musicVideos ?? []
    const soundtracks = musicCollection?.customData?.soundtracks ?? []
    const seriesMusicals = musicCollection?.customData?.seriesMusicals ?? []
    const additionalMusicVideos = musicCollection?.customData?.additionalMusicVideos ?? []

    return {
      props: {
        locale,
        preview,
        heroImages,
        musicVideos,
        soundtracks,
        seriesMusicals,
        additionalMusicVideos,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default MusicPage
